<template>
    <div class="k-vue-editor" :class="{'k-vue-editor__error': null != errors[0]}">
        <QuillEditor v-model:content="value" content-type="html" :id="id" :toolbar="customToolbar" theme="snow" :placeholder="placeholder" />
        <div class="k-vue-editor__errors">{{ errors[0] }}</div>
        <div class="k-vue-editor__chars"><span :class="{'k-vue-editor__chars-red': contentSizeInBytes > MAX_SIZE_LIMIT}">{{ contentSize }}</span>/{{ $t('common.maxLimit') }}</div>
    </div>
</template>

<script setup lang="ts">
import { QuillEditor, Quill } from '@vueup/vue-quill';
// @ts-ignore
// import ImageUploader from 'quill-image-uploader';
import { computed, defineEmits, defineProps, toRef, ref, watch } from 'vue';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import { useField } from 'vee-validate';
import { MAX_SIZE_LIMIT } from '@/hooks/GlobalHooks';
import { toast } from 'vue3-toastify';
import ImageApi from '@/api/v1/ImageApi';

// import 'quill-image-uploader/dist/quill.imageUploader.min.css';
//
// if (!Quill.imports['modules/imageUploader']) {
//     Quill.register('modules/imageUploader', ImageUploader);
// }

interface IOwnProps {
    id: string
    modelValue?: string | number | null | any
    name?: string
    placeholder?: string
}
interface IProps extends IOwnProps {
}

const props = defineProps<IProps>();
const emit = defineEmits(['update:modelValue']);

const { errors, value: fieldValue } = useField(toRef<IOwnProps, any>(props, 'name'), undefined, {
    syncVModel: true
});

const contentSizeInBytes = computed(() => {
    if (!value.value || typeof value.value !== 'string') {
        return 0;
    }
    return new Blob([value.value]).size;
});
const contentSize = computed(() => {
    if (!value.value || typeof value.value !== 'string') {
        return '0 B';
    }

    const bytes = new Blob([value.value]).size;

    if (bytes < 1024) {
        return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(2)} KB`;
    } else {
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
});
// Tutaj ponizej bardziej rozbudowany toolbar ale ma byc wykomentowany bo chca mniej mozliwosci
// const customToolbar = ref([
//     [{ font: [] }],
//     [{ header: [false, 1, 2, 3, 4, 5, 6] }],
//     [{ size: ['small', false, 'large', 'huge'] }],
//     ['bold', 'italic', 'underline', 'strike'],
//     [{ align: '' }, { align: 'center' }, { align: 'right' }, { align: 'justify' }],
//     [{ header: 1 }, { header: 2 }],
//     ['blockquote', 'code-block'],
//     [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
//     [{ script: 'sub' }, { script: 'super' }],
//     [{ indent: '-1' }, { indent: '+1' }],
//     [{ color: [] }, { background: [] }],
//     ['link', 'image', 'video', 'formula'],
//     [{ direction: 'rtl' }],
//     ['clean']
// ]);

// Uproszczony toolbar - tylko potrzebne opcje
const customToolbar = ref([
    [{ 'header': 1 },{ 'header': 2 }], // Tylko H2 dla śródtytułów
    ['bold', 'italic'], // Bold i kursywa
    ['link'], // Linki
    ['image', 'video'], // Obrazy i wideo
]);

const value = computed({
    get() {
        return fieldValue.value !== undefined ? fieldValue.value : props.modelValue;
    },
    set(value) {
        fieldValue.value = value;
        emit('update:modelValue', value);
    }
});

const imageApi = new ImageApi();
const uploadImage = async(file: File) => {
    try {
        const result = await imageApi.uploadImageToS3(file);
        if (!result.isSuccess) {
            toast.error(`${t('articles.toast.errorUploadImageToS3')}`);
            return;
        }
        return result.payload;
    } catch (e) {
        toast.error(`${t('articles.toast.errorUploadImageToS3')}`);
        return;
    }
};
const removeImage = async(url: string) => {
    try {
        const result = await imageApi.deleteImageFromS3(url);
        if (!result.isSuccess) {
            return;
        }
    } catch (e) {
        return;
    }
};
// const modules = ref<any>({
//     name: 'imageUploader',
//     module: ImageUploader,
    // options: {
    //     upload: (file: File) => {
    //         return uploadImage(file);
    //     }
    // }
// });

// watch(
//     () => props.modelValue,
//     (newVal, oldVal) => {
//         if (typeof newVal === 'string' && typeof oldVal === 'string') {
//             if (newVal.length < oldVal.length) {
//                 // Funkcja do wyodrębniania URL-i z atrybutów src w tagach img
//                 const extractImageUrls = (htmlContent: string) => {
//                     const regex = /<img[^>]+src="(https:\/\/[^">]+)"/g;
//                     const urls = [];
//                     let match:any;
//
//                     while ((match = regex.exec(htmlContent))) {
//                         urls.push(match[1]);
//                     }
//
//                     return urls;
//                 };
//
//                 // Pobierz wszystkie URL obrazów ze starej i nowej zawartości
//                 const oldUrls = extractImageUrls(oldVal);
//                 const newUrls = extractImageUrls(newVal);
//
//                 // Znajdź URL-e, które są w starej wersji, ale nie ma ich w nowej
//                 const removedUrls = oldUrls.filter(url => !newUrls.includes(url));
//
//                 if (removedUrls.length > 0) {
//                     removedUrls.forEach((url: string) => {
//                         const prefix = 'https://kasanowa-files.s3.eu-west-2.amazonaws.com/files/-';
//                         const cleanUrl = url.startsWith(prefix) ? url.substring(prefix.length) : url;
//                         removeImage(cleanUrl);
//                     });
//                 }
//             }
//         }
//     }
// );
</script>

<style scoped lang="scss">
.k-vue-editor {
    &__errors {
        font-size: 12px;
        color: $red-400;
        text-transform: lowercase;
        margin-top: 10px;

        &::first-letter {
            text-transform: uppercase;
        }
    }

    &__error {
        &:deep(.ql-toolbar) {
            border-color: $red-400;
        }

        &:deep(.ql-container) {
            border-color: $red-400;
        }
    }

    &__chars {
        color: $primary-400;
    }

    &__chars-red {
        color: $red-400;
    }

    &:deep(.ql-editor) {
        border-radius: 8px;
        min-height: 300px;
    }

    &:deep(.ql-toolbar) {
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
    }

    &:deep(.ql-container) {
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
    }

    &:deep(.ql-editor .ql-video) {
        margin: 0 auto;
    }

    &:deep(.ql-editor img) {
        width: 100%;
        object-fit: cover;
        margin: 0 auto;
    }
}
</style>
